import { Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import { EmailTemplate } from '../../../../../shared/models/generated/ziroModels';
import { ZiroFormAbstractDirective } from '../../../../../forms/ziro-form-abstract.directive';
import { HtmlInputType, ZiroTextComponent, ZiroTextConfig } from '../../../../../forms/fields/text/ziro-text.component';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../../forms/ziro-forms-models';
import { HtmlCheckboxType, ZiroCheckboxConfig } from '../../../../../forms/fields/checkbox/ziro-checkbox.component';
import { Observable, Subscription } from 'rxjs';
import { ZiroMultiTextConfig } from '../../../../../forms/fields/multi-text/ziro-multi-text.component';
import {
  ZiroCodeAreaComponent,
  ZiroCodeAreaConfig,
  ZiroCodeAreaMode,
} from '../../../../../forms/fields/code-area/ziro-code-area.component';
import { EmailTemplatesContext } from '../../../../contexts/email-templates.context';
import { ZiroFormStateService } from '../../../../../forms/ziro-form-state.service';
import { catchError, map } from 'rxjs/operators';
import { DefaultValueService } from '../../../../../shared/services/default-value.service';
import { SafeHtml } from '@angular/platform-browser';
import { VariableEditorModalComponent } from '../../../../../modals/edit-custom-input-text-modal/variable-editor-modal.component';
import { VariableEditorType } from '../../../../../modals/edit-custom-input-text-modal/variable-editor.service';
import { ZiroModalService } from '../../../../../shared/services/ziro-modal.service';
import { ZiroIcons } from '../../../../../shared/models/ziro-icons.enum';
import { ButtonStyles } from '../../../../../button/button.component';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../../../shared/bottom-nav/bottom-nav.service';
import { BottomNavButton } from '../../../../../shared/bottom-nav/bottom-nav.component';
import { Router } from '@angular/router';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HtmlSanitizerService } from '../../../../../shared/services/html-sanitizer.service';

@Component({
  selector: 'ziro-email-template-edit-form',
  templateUrl: 'email-template-edit-form.component.html',
  styleUrls: ['./email-template-edit-form.component.scss'],
  standalone: false,
})
export class EmailTemplateEditFormComponent
  extends ZiroFormAbstractDirective<EmailTemplate>
  implements OnInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private emailTemplatesContext = inject(EmailTemplatesContext);
  private defaultValueService = inject(DefaultValueService);
  private ziroModalService = inject(ZiroModalService);
  private bottomNavService = inject(BottomNavService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private _htmlSanitizerService = inject(HtmlSanitizerService);

  readonly allEmailTemplates = input<EmailTemplate[]>();

  ziroIcons = ZiroIcons;
  ButtonStyles = ButtonStyles;

  subjectHtmlPreview: SafeHtml;
  bodyHtmlPreview: SafeHtml;

  formConfig: ZiroFormConfig = {
    fields: {
      emailTemplateName: {
        id: 'email-template-name',
        label: 'tkey;admin.email_templates.edit_view.name.label',
        dataAutomation: 'email-template-name-input',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        required: true,
        validation: [
          {
            validator: (val: string) => {
              return !!val &&
                this.allEmailTemplates().some(
                  (template) =>
                    template.id !== this.entity.id && template.emailTemplateName.toLowerCase() === val.toLowerCase(),
                )
                ? ZiroFormsValidationState.INVALID
                : ZiroFormsValidationState.VALID;
            },
            message: 'tkey;admin.email_templates.edit_view.name.validation.message',
          },
        ],
      },
      senderName: {
        id: 'email-sender-name',
        label: 'tkey;admin.email_templates.edit_view.sender_name.label',
        dataAutomation: 'email-sender-name-input',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        helpText: 'tkey;admin.email_templates.edit_view.sender_name.helptext',
        required: true,
      },
      is360ViewEndUserRecipient: {
        id: 'is-360-view-end-user-recipient',
        label: 'tkey;admin.email_templates.edit_view.recipients.360_view_enduser.label',
        dataAutomation: 'email-template-is-360-view-enduser-recipient-checkbox',
        helpText: 'tkey;admin.email_templates.edit_view.recipients.360_view_enduser.helptext',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.CHECKBOX }),
      },
      otherRecipients: {
        id: 'other-recipients',
        label: 'tkey;admin.email_templates.edit_view.recipients.other.label',
        dataAutomation: 'email-template-other-recipients-multitext',
        helpText: 'tkey;admin.email_templates.edit_view.recipients.other.helptext',
        componentConfig: new ZiroMultiTextConfig(),
        validation: [
          {
            validator: (values: string[]): ZiroFormsValidationState => {
              return values.every((value: string) => {
                return (
                  !value ||
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
                    value,
                  )
                );
              })
                ? ZiroFormsValidationState.VALID
                : ZiroFormsValidationState.INVALID;
            },
            message: 'tkey;validators.global.email.invalid.error',
          },
          {
            validator: (val: string[], is360ViewEndUserRecipient: boolean) => {
              return !is360ViewEndUserRecipient && val.every((v) => !v)
                ? ZiroFormsValidationState.INVALID
                : ZiroFormsValidationState.VALID;
            },
            message: 'tkey;admin.email_templates.edit_view.recipients.validation.message',
            injectValuesFromFields: ['is360ViewEndUserRecipient'],
          },
        ],
      },
      emailSubject: {
        id: 'email-subject',
        label: 'tkey;admin.email_templates.edit_view.subject.label',
        dataAutomation: 'email-template-subject-input',
        helpText: 'tkey;admin.email_templates.edit_view.subject.helptext',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        required: true,
        validation: [
          {
            validator: (val: string) => {
              this._emailSubjectUnsafeHtml = this._htmlSanitizerService.checkForUnsafeHtml(val);
              return !this._emailSubjectUnsafeHtml ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
            },
            message: () => {
              return {
                content: 'tkey;validators.global.unsafe_html',
                params: { html: this._emailSubjectUnsafeHtml },
              };
            },
          },
        ],
      },
      emailBody: {
        id: 'email-body',
        label: 'tkey;admin.email_templates.edit_view.body.label',
        dataAutomation: 'email-template-body-codearea',
        helpText: 'tkey;admin.email_templates.edit_view.body.helptext',
        componentConfig: new ZiroCodeAreaConfig({ mode: ZiroCodeAreaMode.HTML }),
        required: true,
        validation: [
          {
            validator: (val: string) => {
              this._emailBodyUnsafeHtml = this._htmlSanitizerService.checkForUnsafeHtml(val);
              return !this._emailBodyUnsafeHtml ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
            },
            message: () => {
              return {
                content: 'tkey;validators.global.unsafe_html',
                params: { html: this._emailBodyUnsafeHtml },
              };
            },
          },
        ],
      },
    },
  };

  private _subscription = new Subscription();
  private _isCopy: boolean;
  private _emailSubjectUnsafeHtml: string;
  private _emailBodyUnsafeHtml: string;

  ngOnInit(): void {
    this._isCopy = this.router.url.includes('copy');
    this._initBottomNav(this.entity);

    const formUpdateSub = this.ziroFormsUpdate$.subscribe((update) => {
      const newSubject = update.new.emailSubject;
      if (newSubject != null) {
        this.subjectHtmlPreview = this._generatePreview(newSubject);
      }
      const newBody = update.new.emailBody;
      if (newBody != null) {
        this.bodyHtmlPreview = this._generatePreview(newBody);
      }

      if (this.isFormSubmitted) {
        this.bottomNavService.dispatch(
          new BottomNavUpdateState({
            hasValidationError: !this.isFormValid(),
          }),
        );
      }
    });
    this._subscription.add(formUpdateSub);

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() => {
      this.bottomNavService.dispatch(
        new BottomNavUpdateState({
          hasValidationError: !this.isFormValid(),
        }),
      );
    });
    this._subscription.add(formSubmittedSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscription.unsubscribe();
  }

  openVariableEditorModal(fieldId: 'emailSubject' | 'emailBody'): void {
    const modalInstance = this.ziroModalService.openCustomModal(
      VariableEditorModalComponent,
      {
        type: VariableEditorType.CISCO,
      },
      { size: 'lg' },
    );
    modalInstance.subscribe((newValue: any) => {
      const value = newValue.value;
      const fieldComponent = this.fieldComponents.find((field) => field.fieldId() === fieldId);
      if (fieldId === 'emailSubject') {
        (fieldComponent as ZiroTextComponent).insertText(value);
      } else {
        (fieldComponent as ZiroCodeAreaComponent).insertText(value);
      }
    });
  }

  protected submit(): Observable<void> {
    this._setButtonsPending(true);
    return this._saveEmailTemplate(this.entity).pipe(
      map(() => {
        this._onSaveComplete(this.entity);
      }),
      catchError((err) => {
        this._setButtonsPending(false);
        throw err;
      }),
    );
  }

  private _generatePreview(unparsedText: string): SafeHtml {
    const htmlPreview = this.defaultValueService.generatePreview(unparsedText);
    return this._htmlSanitizerService.sanitizeAndTrustHtml(htmlPreview);
  }

  private _saveEmailTemplate(emailTemplate: EmailTemplate): Observable<void | number> {
    if (emailTemplate.id && !this._isCopy) {
      return this.emailTemplatesContext.updateTemplate(emailTemplate.id, emailTemplate);
    } else {
      return this.emailTemplatesContext.createTemplate(emailTemplate);
    }
  }

  private _initBottomNav(emailTemplate: EmailTemplate): void {
    const bottomNavButtons: BottomNavButton[] = [];
    bottomNavButtons.push({
      id: 'email-template-cancel',
      label: 'tkey;global.button.cancel.text',
      dataAutomation: 'email-template-edit-bottom-nav-cancel-button',
      buttonClass: ButtonStyles.DEFAULT,
      cb: () => this._onCancelClicked(),
    });

    if (emailTemplate.id && !this._isCopy) {
      bottomNavButtons.push({
        id: 'email-template-delete',
        label: 'tkey;global.button.delete.text',
        dataAutomation: 'email-template-edit-bottom-nav-delete-button',
        buttonClass: ButtonStyles.DANGER,
        icon: ZiroIcons.DELETE_SMALL,
        cb: () => this._onDeleteClicked(),
      });
    }

    bottomNavButtons.push({
      id: 'email-template-save',
      label: 'tkey;global.button.save.text',
      dataAutomation: 'email-template-edit-bottom-nav-save-button',
      buttonClass: ButtonStyles.PRIMARY,
      icon: ZiroIcons.SAVE_SUCCESS,
      cb: () => this._onSaveClicked(),
    });

    this.bottomNavService.dispatch(new BottomNavUpdateButtonsList(bottomNavButtons));
  }

  private _onSaveClicked(): void {
    this._validateAndSubmitSource.next(true);
  }

  private _returnToTable(): void {
    this.router.navigateByUrl('/admin/cisco/email-templates');
  }

  private _onCancelClicked(): void {
    this._returnToTable();
  }

  private _onDeleteClicked(): void {
    this.emailTemplatesContext.deleteTemplate(this.entity).subscribe((wasDeleted) => {
      if (wasDeleted) {
        this._returnToTable();
      }
    });
  }

  private _onSaveComplete(emailTemplate: EmailTemplate): void {
    this.toastService.pushSaveToast(
      'tkey;admin.email_templates.edit_view.toast.message.type',
      emailTemplate.emailTemplateName,
      ZiroIcons.SEND_EMAIL,
    );
    this._returnToTable();
  }

  private _setButtonsPending(isPending: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'email-template-save',
        state: {
          pending: isPending,
          buttonDisableState: {
            disabled: isPending,
          },
        },
      }),
    );
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'email-template-cancel',
        state: {
          buttonDisableState: {
            disabled: isPending,
          },
        },
      }),
    );
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'email-template-delete',
        state: {
          buttonDisableState: {
            disabled: isPending,
          },
        },
      }),
    );
  }
}
